import { QRCodeSVG } from 'qrcode.react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { PasswordChangeForm } from '@/features/security/PasswordChangeForm'
import { RecoveryCodes } from '@/features/security/RecoveryCodes'
import {
  disabledSchema,
  enabledSchema,
  enrollmentSchema,
  finalSession,
  microsoftProofSchema,
  proofSchema,
  SecurityError,
  securityErrorKey,
  securityStatusSchema,
  useSensitiveRequest,
} from '@/features/security/securityClient'
import type {
  Enrollment,
  FactorMethod,
  SecurityAction,
  SecurityStatus,
} from '@/features/security/securityClient'
import { FactorFields, SecretField } from '@/features/security/SecurityFields'
import { useAppDispatch } from '@/hooks/redux'
import { api } from '@/store/api'
import { removeSession, setSession } from '@/store/api/Auth/Auth.slice'

type Flow =
  | { kind: 'idle' }
  | { kind: 'proof'; action: SecurityAction }
  | { kind: 'microsoft' }
  | { kind: 'ready'; action: SecurityAction; ticket: string }
  | { kind: 'enrollment'; enrollment: Enrollment }
  | { kind: 'codes'; codes: string[] }

function EnrollmentForm({
  enrollment,
  busy,
  onConfirm,
  onCancel,
}: {
  enrollment: Enrollment
  busy: boolean
  onConfirm: (code: string) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [code, setCode] = useState('')
  const [expired, setExpired] = useState(false)
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    heading.current?.focus()
    const timer = setTimeout(() => {
      setExpired(true)
      setCode('')
    }, enrollment.expiresIn * 1000)
    return () => clearTimeout(timer)
  }, [enrollment])
  return (
    <form
      className="sentry-block flex max-w-lg flex-col gap-4"
      data-sentry-block="true"
      onSubmit={(event) => {
        event.preventDefault()
        if (!expired && !busy) {
          onConfirm(code.trim())
          setCode('')
        }
      }}
    >
      <h2 ref={heading} tabIndex={-1} className="text-xl font-medium">
        {t('security.setupPending')}
      </h2>
      <p>{t('security.setupInstructions')}</p>
      <div className="max-w-full self-start rounded-lg bg-white p-3">
        <QRCodeSVG
          value={enrollment.otpauthUri}
          size={192}
          marginSize={4}
          role="img"
          aria-label={t('security.qrLabel')}
        />
      </div>
      <p className="text-sm">{t('security.manualKey')}</p>
      <code className="break-all rounded border border-neutralColor5 p-3">
        {enrollment.manualKey}
      </code>
      <SecretField
        label={t('security.authenticatorCode')}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        autoComplete="one-time-code"
        required
        inputMode="numeric"
        pattern="[0-9]{6}"
        maxLength={6}
        disabled={busy || expired}
      />
      {expired && <p role="alert">{t('security.enrollmentExpired')}</p>}
      <div className="flex flex-wrap gap-3">
        <Button type="submit" isLoading={busy} disabled={busy || expired}>
          {t('security.confirmSetup')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={busy}
        >
          {t('security.cancel')}
        </Button>
      </div>
    </form>
  )
}

export function SecurityPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const request = useSensitiveRequest()
  const [status, setStatus] = useState<SecurityStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [statusFailed, setStatusFailed] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [flow, setFlow] = useState<Flow>({ kind: 'idle' })
  const [currentPassword, setCurrentPassword] = useState('')
  const [method, setMethod] = useState<FactorMethod>('totp')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const lock = useRef(false)
  const continued = useRef(false)
  const flowHeading = useRef<HTMLDivElement>(null)

  const signOut = useCallback(
    (message: string) => {
      dispatch(removeSession())
      dispatch(api.util.resetApiState())
      toast.info(t(message))
      navigate(AppRoutes.login, { replace: true })
    },
    [dispatch, navigate, t],
  )

  const loadStatus = useCallback(
    async (unauthorizedMessage = 'security.signInAgain') => {
      setLoading(true)
      setStatusFailed(false)
      try {
        setStatus(
          await request('/api/v1/auth/security', securityStatusSchema, {
            method: 'GET',
            authenticated: true,
          }),
        )
      } catch (e) {
        setStatus(null)
        setStatusFailed(true)
        if (e instanceof SecurityError && e.status === 401)
          signOut(unauthorizedMessage)
      } finally {
        setLoading(false)
      }
    },
    [request, signOut],
  )

  useEffect(() => {
    void loadStatus()
  }, [loadStatus])
  useEffect(() => {
    if (
      !continued.current &&
      new URLSearchParams(window.location.search).get('reauth') === 'required'
    ) {
      continued.current = true
      window.history.replaceState(
        window.history.state,
        '',
        AppRoutes.settingsSecurity,
      )
      setFlow({ kind: 'microsoft' })
    }
  }, [])
  useEffect(() => {
    flowHeading.current?.focus()
  }, [flow.kind])
  useEffect(() => {
    if (flow.kind !== 'codes') return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [flow.kind])

  const clearProof = () => {
    setCurrentPassword('')
    setCode('')
  }
  const failure = async (e: unknown, mutation = false) => {
    clearProof()
    setError(securityErrorKey(e))
    if (e instanceof SecurityError && mutation && e.uncertain) {
      setNotice('security.uncertain')
      setFlow({ kind: 'idle' })
      setStatus(null)
      await loadStatus('security.uncertainSignIn')
    } else if (e instanceof SecurityError && e.status === 401) {
      signOut(mutation ? 'security.uncertainSignIn' : 'security.signInAgain')
    } else if (
      e instanceof SecurityError &&
      ['MFA_REQUIRED', 'MFA_ALREADY_ENABLED', 'MFA_NOT_ENABLED'].includes(
        e.code,
      )
    ) {
      setFlow({ kind: 'idle' })
      setStatus(null)
      await loadStatus()
    }
  }
  const run = async (task: () => Promise<void>) => {
    if (lock.current) return
    lock.current = true
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await task()
    } finally {
      lock.current = false
      setBusy(false)
    }
  }
  const completeAction = async (action: SecurityAction, ticket: string) => {
    try {
      if (action === 'enroll_mfa') {
        const enrollment = await request(
          '/api/v1/auth/security/mfa/enrollment',
          enrollmentSchema,
          {
            authenticated: true,
            body: { reauthToken: ticket },
          },
        )
        setFlow({ kind: 'enrollment', enrollment })
      } else if (action === 'disable_mfa') {
        const result = await request(
          '/api/v1/auth/security/mfa/disable',
          disabledSchema,
          {
            authenticated: true,
            body: { reauthToken: ticket },
          },
        )
        dispatch(setSession(finalSession(result)))
        setFlow({ kind: 'idle' })
        setNotice('security.disabledConfirmed')
        await loadStatus()
      } else {
        const result = await request(
          '/api/v1/auth/security/mfa/recovery-codes/rotate',
          enabledSchema,
          {
            authenticated: true,
            body: { reauthToken: ticket },
          },
        )
        dispatch(setSession(finalSession(result)))
        setFlow({ kind: 'codes', codes: result.recoveryCodes })
        await loadStatus()
      }
    } catch (e) {
      setFlow({ kind: 'idle' })
      await failure(e, true)
    }
  }
  const actionLabel = (action: SecurityAction) =>
    t(`security.actions.${action}`)
  const canMicrosoft =
    status?.passwordManagement === 'microsoft' ||
    status?.passwordManagement === 'mixed' ||
    status?.passwordManagement === 'unknown'
  const canNative = status?.passwordManagement !== 'microsoft'

  return (
    <WeScreen
      useNextButton={false}
      useSkipButton={false}
      hideHeaderOnMobile
      onClickBack={() => {
        if (flow.kind === 'codes' && !window.confirm(t('security.leaveCodes')))
          return
        navigate(AppRoutes.settings)
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto rounded-2xl bg-baseColorHighlight p-4 text-neutralColor sm:p-6">
        <div>
          <h1 className="text-2xl font-medium">{t('security.title')}</h1>
          <p className="mt-2 text-sm text-neutralColor60">
            {t('security.subtitle')}
          </p>
        </div>
        {error && (
          <p role="alert" className="text-sm text-dynamicRed">
            {t(error)}
          </p>
        )}
        {notice && (
          <p role="status" className="text-sm">
            {t(notice)}
          </p>
        )}
        {loading && <p role="status">{t('security.loading')}</p>}
        {statusFailed && (
          <div role="alert" className="flex flex-col items-start gap-3">
            <p>{t('security.statusFailed')}</p>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void loadStatus()}
            >
              {t('security.retry')}
            </Button>
          </div>
        )}
        {flow.kind === 'codes' ? (
          <RecoveryCodes
            codes={flow.codes}
            onSaved={() => {
              setFlow({ kind: 'idle' })
              setNotice('security.codesSaved')
            }}
          />
        ) : (
          status && (
            <>
              {flow.kind === 'idle' && (
                <PasswordChangeForm
                  status={status}
                  onSuccess={() => signOut('security.passwordChanged')}
                  onError={(e, mutation) => void failure(e, mutation)}
                />
              )}
              <section className="flex flex-col gap-4">
                <h2 className="text-xl font-medium">
                  {t('security.mfaTitle')}
                </h2>
                <p className="font-medium">
                  {t(
                    status.mfaEnabled
                      ? 'security.enabled'
                      : 'security.notEnabled',
                  )}
                </p>
                <p className="max-w-2xl text-sm text-neutralColor60">
                  {t('security.mfaHelp')}
                </p>
                {status.mfaEnabled && (
                  <p className="text-sm">
                    {t('security.recoveryRemaining', {
                      count: status.recoveryCodesRemaining,
                    })}
                  </p>
                )}
                {!status.mfaAvailable && (
                  <p role="status">{t('security.unavailable')}</p>
                )}
                {!status.emailVerified && (
                  <p role="status">{t('security.verifyEmailFirst')}</p>
                )}
                {flow.kind === 'idle' && (
                  <div className="flex flex-wrap gap-3">
                    {!status.mfaEnabled ? (
                      <Button
                        type="button"
                        disabled={!status.mfaAvailable || !status.emailVerified}
                        onClick={() => {
                          clearProof()
                          setFlow({ kind: 'proof', action: 'enroll_mfa' })
                          setError('')
                        }}
                      >
                        {t('security.setup')}
                      </Button>
                    ) : (
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={!status.mfaAvailable}
                          onClick={() => {
                            clearProof()
                            setFlow({
                              kind: 'proof',
                              action: 'rotate_recovery_codes',
                            })
                            setError('')
                          }}
                        >
                          {t('security.actions.rotate_recovery_codes')}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={!status.mfaAvailable}
                          onClick={() => {
                            clearProof()
                            setFlow({ kind: 'proof', action: 'disable_mfa' })
                            setError('')
                          }}
                        >
                          {t('security.actions.disable_mfa')}
                        </Button>
                      </>
                    )}
                  </div>
                )}
                <div ref={flowHeading} tabIndex={-1}>
                  {(flow.kind === 'proof' || flow.kind === 'microsoft') && (
                    <form
                      className="flex max-w-lg flex-col gap-4"
                      onSubmit={(event) => {
                        event.preventDefault()
                        void run(async () => {
                          try {
                            if (flow.kind === 'microsoft') {
                              const proof = await request(
                                '/api/v1/auth/security/reauth/microsoft/complete',
                                microsoftProofSchema,
                                {
                                  authenticated: true,
                                  body: status.mfaEnabled
                                    ? { method, code: code.trim() }
                                    : {},
                                },
                              )
                              clearProof()
                              setFlow({
                                kind: 'ready',
                                action: proof.action,
                                ticket: proof.reauthToken,
                              })
                            } else {
                              const proof = await request(
                                '/api/v1/auth/security/reauth',
                                proofSchema,
                                {
                                  authenticated: true,
                                  body: {
                                    action: flow.action,
                                    currentPassword,
                                    ...(status.mfaEnabled
                                      ? { method, code: code.trim() }
                                      : {}),
                                  },
                                },
                              )
                              if (proof.action !== flow.action)
                                throw new SecurityError('INVALID_RESPONSE', 200)
                              clearProof()
                              await completeAction(
                                flow.action,
                                proof.reauthToken,
                              )
                            }
                          } catch (e) {
                            await failure(e)
                          }
                        })
                      }}
                    >
                      <h3 className="text-lg font-medium">
                        {flow.kind === 'proof'
                          ? actionLabel(flow.action)
                          : t('security.microsoftContinue')}
                      </h3>
                      <p className="text-sm">{t('security.reauthHelp')}</p>
                      {flow.kind === 'proof' &&
                        flow.action !== 'enroll_mfa' && (
                          <p className="text-sm">
                            {t(
                              flow.action === 'disable_mfa'
                                ? 'security.disableWarning'
                                : 'security.rotateWarning',
                            )}
                          </p>
                        )}
                      {flow.kind === 'proof' && canNative && (
                        <SecretField
                          label={t('security.currentPassword')}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          required
                          autoComplete="current-password"
                          disabled={busy}
                        />
                      )}
                      {status.mfaEnabled &&
                        (flow.kind === 'microsoft' || canNative) && (
                          <FactorFields
                            method={method}
                            code={code}
                            onCode={setCode}
                            disabled={busy}
                            onMethod={(next) => {
                              setMethod(next)
                              setCode('')
                            }}
                          />
                        )}
                      <div className="flex flex-wrap gap-3">
                        {(flow.kind === 'microsoft' || canNative) && (
                          <Button
                            type="submit"
                            disabled={busy}
                            isLoading={busy}
                          >
                            {flow.kind === 'microsoft'
                              ? t('security.continue')
                              : actionLabel(flow.action)}
                          </Button>
                        )}
                        {flow.kind === 'proof' && canMicrosoft && (
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={busy}
                            onClick={() =>
                              void run(async () => {
                                try {
                                  clearProof()
                                  const result = await request(
                                    '/api/v1/auth/security/reauth/microsoft/start',
                                    z.object({ redirectUrl: z.string().url() }),
                                    {
                                      authenticated: true,
                                      body: { action: flow.action },
                                    },
                                  )
                                  const url = new URL(result.redirectUrl)
                                  if (
                                    url.username ||
                                    url.password ||
                                    (url.protocol !== 'https:' &&
                                      !(
                                        url.protocol === 'http:' &&
                                        ['localhost', '127.0.0.1'].includes(
                                          url.hostname,
                                        )
                                      ))
                                  )
                                    throw new SecurityError(
                                      'INVALID_RESPONSE',
                                      200,
                                    )
                                  window.location.assign(url.href)
                                } catch (e) {
                                  await failure(e)
                                }
                              })
                            }
                          >
                            {t('security.reauthMicrosoft')}
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={busy}
                          onClick={() => {
                            clearProof()
                            setFlow({ kind: 'idle' })
                          }}
                        >
                          {t('security.cancel')}
                        </Button>
                      </div>
                    </form>
                  )}
                  {flow.kind === 'ready' && (
                    <div className="flex max-w-lg flex-col gap-4">
                      <h3 className="text-lg font-medium">
                        {actionLabel(flow.action)}
                      </h3>
                      {flow.action !== 'enroll_mfa' && (
                        <p>
                          {t(
                            flow.action === 'disable_mfa'
                              ? 'security.disableWarning'
                              : 'security.rotateWarning',
                          )}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-3">
                        <Button
                          type="button"
                          disabled={busy}
                          isLoading={busy}
                          onClick={() =>
                            void run(async () =>
                              completeAction(flow.action, flow.ticket),
                            )
                          }
                        >
                          {actionLabel(flow.action)}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={busy}
                          onClick={() => setFlow({ kind: 'idle' })}
                        >
                          {t('security.cancel')}
                        </Button>
                      </div>
                    </div>
                  )}
                  {flow.kind === 'enrollment' && (
                    <EnrollmentForm
                      enrollment={flow.enrollment}
                      busy={busy}
                      onCancel={() => {
                        setFlow({ kind: 'idle' })
                        setError('')
                      }}
                      onConfirm={(confirmationCode) =>
                        void run(async () => {
                          try {
                            const result = await request(
                              '/api/v1/auth/security/mfa/enrollment/confirm',
                              enabledSchema,
                              {
                                authenticated: true,
                                body: {
                                  enrollmentToken:
                                    flow.enrollment.enrollmentToken,
                                  code: confirmationCode,
                                },
                              },
                            )
                            dispatch(setSession(finalSession(result)))
                            setFlow({
                              kind: 'codes',
                              codes: result.recoveryCodes,
                            })
                            await loadStatus()
                          } catch (e) {
                            await failure(e, true)
                          }
                        })
                      }
                    />
                  )}
                </div>
              </section>
            </>
          )
        )}
      </div>
    </WeScreen>
  )
}
